import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdClose, MdRefresh } from 'react-icons/md';
import { tradingService } from '../../../services/tradingService';
import { useSortableRows, SortableTh } from '../../../components/tables/SortableTable';

// Security Lifecycle → History (2026-10-21, owner: "what has happened till now … 7 stocks delisted, 3 renamed,
// 5 merged … and can I stop it that way"). A summary of every lifecycle event since we started, one row per event,
// and per event the sleeves it touched with a Stop button. Every number opens what is behind it (house rule).
// Backend: services/lifecycle_review.lifecycle_history / event_sleeves / stop_event_sleeves.

const fmtQty = (v) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }));
const fmtUsd = (v) => (v == null ? '' : `≈ $${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const day = (iso) => (iso ? String(iso).slice(0, 10) : '—');

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  applied: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

function StatusChip({ status }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${STATUS_STYLE[status] || STATUS_STYLE.rejected}`}>
      {status === 'rejected' ? 'false alarm' : status}
    </span>
  );
}

// A summary tile: the number is a button that filters the table below to what it counts.
function Tile({ label, value, sub, onClick, active, tone }) {
  const toneCls = tone === 'bad' && value > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100';
  return (
    <button onClick={onClick}
      className={`text-left p-3 rounded-lg border transition-colors ${active
        ? 'border-[#c9a96e] bg-[#c9a96e]/10' : 'border-gray-200 dark:border-gray-700 hover:border-[#c9a96e]/60'}`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`font-serif text-2xl tabular-nums ${toneCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 dark:text-gray-400">{sub}</div>}
    </button>
  );
}

export function EventSleevesModal({ reviewId, title, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);           // strategy id, or 'all'

  const load = useCallback(async () => {
    setErr('');
    try {
      const { data: d } = await tradingService.lifecycleSleeves(reviewId);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not load the sleeves.');
    }
  }, [reviewId]);
  useEffect(() => { load(); }, [load]);

  const sleeves = data?.sleeves || [];
  const onDead = sleeves.filter(s => s.on_dead && s.stoppable);

  const stop = async (rows) => {
    if (!rows.length) return;
    const lines = rows.map(r => `• ${r.name} (${r.account_name})${r.batch_pending ? ' — order sheet out: stop applied at fills / No fills' : ''}${r.holdings.length
      ? ` — sells ${r.holdings.map(h => `${fmtQty(h.qty)} ${h.symbol}`).join(', ')} at the next open` : ' — holds nothing'}`).join('\n');
    if (!window.confirm(`Stop ${rows.length} sleeve(s)?\n\n${lines}\n\nEach is deactivated exactly like on the Strategies page: its staged orders are cancelled, whatever it holds is sold at the next open, and the cash returns to the account as free cash. The rebalance does not redeploy that cash on its own.`)) return;
    setBusy(rows.length > 1 ? 'all' : rows[0].strategy_id); setErr('');
    try {
      const { data: out } = await tradingService.lifecycleStopSleeves(reviewId, rows.map(r => r.strategy_id));
      if (out?.skipped?.length) setErr(out.skipped.map(s => `#${s.strategy_id}: ${s.reason}`).join(' · '));
      // Wishlist #40: a model sleeve whose order sheet is out — the stop is recorded, applied at fills / No fills
      if (out?.pending?.length) window.alert(`${out.pending.length} sleeve(s) keep running for now: their account\u2019s order sheet is out with the operator. Each stop is applied when that sheet is resolved (fills uploaded or No fills), and its holdings are sold on the next sheet.`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Stop failed.');
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex sm:items-center sm:justify-center">
      <div className="w-full h-full sm:h-auto sm:max-h-[85vh] sm:max-w-2xl sm:rounded-lg bg-white dark:bg-gray-900 sm:border sm:border-gray-200 dark:sm:border-gray-700 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <div className="font-serif text-lg text-gray-900 dark:text-gray-100">{title || data?.ticker} · sleeves</div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Every sleeve this event touched, as it stands now</div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" title="Close"><MdClose size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-3">
          {data?.class === 'terminal' && (
            <p className="text-[13px] text-gray-600 dark:text-gray-400">
              A sleeve whose stock is dead can never trade it again. Stopping it deactivates it like the Strategies page does:
              staged orders are cancelled and whatever it holds — usually the ETF its money was parked in — is <span className="font-semibold">sold at the next open</span>;
              the cash returns to the account as free cash.
            </p>
          )}
          {err && <div className="text-[13px] text-red-600 bg-red-50 dark:bg-red-900/20 rounded p-2">{err}</div>}
          {!data && !err && <div className="text-[13px] text-gray-400">Loading…</div>}
          {data && sleeves.length === 0 && <div className="text-[13px] text-gray-400">No sleeve ever used this stock.</div>}
          <div className="space-y-2">
            {sleeves.map(r => (
              <div key={r.strategy_id} className="flex items-start justify-between gap-3 rounded border border-gray-200 dark:border-gray-700 p-2">
                <div className="min-w-0">
                  <div className="text-[13px] text-gray-900 dark:text-gray-100 break-words">{r.name} <span className="text-gray-400">#{r.strategy_id}</span></div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">
                    {r.account_name}{r.is_model ? ' · model (External Book)' : ''}
                    {' · '}{r.holdings.length
                      ? `holds ${r.holdings.map(h => `${fmtQty(h.qty)} ${h.symbol} ${fmtUsd(h.value)}`).join(', ')}`
                      : 'holds nothing'}
                    {r.staged_orders ? ` · ${r.staged_orders} staged order(s)` : ''}
                    {r.batch_pending && !r.stop_pending && <span className="ml-1 text-amber-600 dark:text-amber-400">· order sheet out — a stop waits for fills / No fills</span>}
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {r.on_dead && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">on dead stock</span>}
                  {!r.is_active ? (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-gray-500/15 text-gray-600 dark:text-gray-300">
                      {r.deployment_status === 'closing' ? 'closing' : 'stopped'}{r.stopped_here ? ' here' : ''}
                    </span>
                  ) : r.stop_pending ? (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                          title="Applied when the account's order sheet is resolved (fills uploaded or No fills)">
                      stop requested
                    </span>
                  ) : r.stoppable ? (
                    <button disabled={busy != null} onClick={() => stop([r])}
                      className="px-3 py-1 rounded bg-[#c9a96e] text-white text-[12px] font-medium hover:opacity-90 disabled:opacity-50">
                      {busy === r.strategy_id ? 'Stopping…' : 'Stop'}
                    </button>
                  ) : (
                    <span className="text-[11px] text-green-600 dark:text-green-400">active</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-end gap-2">
          {onDead.length > 1 && (
            <button disabled={busy != null} onClick={() => stop(onDead)}
              className="px-3 py-1.5 rounded bg-red-600 text-white text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
              {busy === 'all' ? 'Stopping…' : `Stop all ${onDead.length} on the dead stock`}
            </button>
          )}
          <button onClick={onClose} className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-[13px] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">Done</button>
        </div>
      </div>
    </div>
  );
}

export default function LifecycleHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState(null);       // {type: 'kind'|'false'|'on_dead'|'stopped'|'pending', value}
  const [open, setOpen] = useState(null);           // {id, title}

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data: d } = await tradingService.lifecycleHistory();
      setData(d);
    } catch (e) {
      setErr('Could not load the history.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  const events = useMemo(() => {
    const all = data?.events || [];
    if (!filter) return all;
    if (filter.type === 'kind') return all.filter(e => e.kind === filter.value && e.status !== 'rejected' && !e.duplicate_of);
    if (filter.type === 'false') return all.filter(e => e.status === 'rejected');
    if (filter.type === 'on_dead') return all.filter(e => e.sleeves_still_on_dead > 0);
    if (filter.type === 'stopped') return all.filter(e => e.sleeves_stopped_here > 0);
    if (filter.type === 'pending') return all.filter(e => e.status === 'pending');
    if (filter.type === 'events') return all.filter(e => e.status !== 'rejected' && !e.duplicate_of);
    return all;
  }, [data, filter]);

  const cols = useMemo(() => [
    { key: 'ticker', type: 'string', get: r => r.ticker },
    { key: 'kind', type: 'string', get: r => r.kind_label },
    { key: 'date', type: 'string', get: r => r.event_date || '' },
    { key: 'status', type: 'string', get: r => r.status },
    { key: 'detected', type: 'string', get: r => r.detected_at || '' },
    { key: 'sleeves', type: 'number', get: r => r.sleeves },
    { key: 'on_dead', type: 'number', get: r => r.sleeves_still_on_dead },
    { key: 'stopped', type: 'number', get: r => r.sleeves_stopped_here },
    { key: 'holders', type: 'number', get: r => r.holders_closed },
  ], []);
  const { sorted, toggle, indicator } = useSortableRows(events, cols);
  const pick = (f) => setFilter(cur => (cur && cur.type === f.type && cur.value === f.value ? null : f));
  const isOn = (f) => !!filter && filter.type === f.type && filter.value === f.value;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-gray-500 dark:text-gray-400">
          Every delisting, merger, take-private and rename the system has detected since it started, what was decided,
          and the sleeves each one touched. Click any number to see what is behind it.
        </p>
        <button onClick={load} className="p-2 text-gray-500 hover:text-[#c9a96e] shrink-0" title="Refresh">
          <MdRefresh className={loading ? 'animate-spin' : ''} size={20} />
        </button>
      </div>
      {err && <div className="text-[13px] text-red-600">{err}</div>}

      {s && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {s.by_kind.map(k => (
              <Tile key={k.kind} label={k.label} value={k.events}
                sub={`${k.sleeves} sleeve${k.sleeves === 1 ? '' : 's'}${k.pending ? ` · ${k.pending} pending` : ''}`}
                onClick={() => pick({ type: 'kind', value: k.kind })} active={isOn({ type: 'kind', value: k.kind })} />
            ))}
            <Tile label="False alarms" value={s.false_alarms} sub="rejected on review"
              onClick={() => pick({ type: 'false' })} active={isOn({ type: 'false' })} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <Tile label="Events" value={s.events} sub={`${s.pending} awaiting a decision`}
              onClick={() => pick({ type: 'events' })} active={isOn({ type: 'events' })} />
            <Tile label="Awaiting decision" value={s.pending} onClick={() => pick({ type: 'pending' })} active={isOn({ type: 'pending' })} tone="bad" />
            <Tile label="Sleeves affected" value={s.sleeves_affected} sub={`${s.accounts_affected} account(s)`}
              onClick={() => pick({ type: 'events' })} active={false} />
            <Tile label="Still on a dead stock" value={s.sleeves_still_on_dead} sub="active, frozen — stop them"
              onClick={() => pick({ type: 'on_dead' })} active={isOn({ type: 'on_dead' })} tone="bad" />
            <Tile label="Stopped from here" value={s.sleeves_stopped}
              onClick={() => pick({ type: 'stopped' })} active={isOn({ type: 'stopped' })} />
          </div>
        </>
      )}

      {filter && (
        <div className="text-[12px] text-gray-500">
          Filtered · <button onClick={() => setFilter(null)} className="text-[#c9a96e] hover:underline">show all events</button>
        </div>
      )}

      <div className="overflow-x-auto scrollbar-thin rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-[12px]">
          <thead className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <SortableTh label="Ticker" colKey="ticker" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
              <SortableTh label="Event" colKey="kind" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
              <SortableTh label="Date" colKey="date" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
              <SortableTh label="Status" colKey="status" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
              <SortableTh label="Detected" colKey="detected" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
              <SortableTh label="Sleeves" colKey="sleeves" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="On dead stock" colKey="on_dead" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="Stopped here" colKey="stopped" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="Holders closed" colKey="holders" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <th className="px-3 py-2 text-left">Accounts</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(e => (
              <tr key={e.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40">
                <td className="px-3 py-2 font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                  {e.ticker}{e.successor ? <span className="font-normal text-gray-500"> → {e.successor}</span> : ''}
                </td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{e.kind_label}</td>
                <td className="px-3 py-2 tabular-nums text-gray-500 whitespace-nowrap">{e.event_date || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <StatusChip status={e.status} />
                  {e.duplicate_of && <span className="ml-1 text-[10px] text-gray-400">dup of #{e.duplicate_of}</span>}
                </td>
                <td className="px-3 py-2 tabular-nums text-gray-500 whitespace-nowrap">{day(e.detected_at)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  <button onClick={() => setOpen({ id: e.id, title: e.ticker })} disabled={!e.sleeves}
                    className="text-[#c9a96e] hover:underline disabled:text-gray-400 disabled:no-underline">{e.sleeves}</button>
                </td>
                <td className={`px-3 py-2 text-right tabular-nums ${e.sleeves_still_on_dead ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400'}`}>
                  {e.sleeves_still_on_dead
                    ? <button onClick={() => setOpen({ id: e.id, title: e.ticker })} className="hover:underline">{e.sleeves_still_on_dead}</button>
                    : 0}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{e.sleeves_stopped_here}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{e.holders_closed}</td>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{e.accounts.join(', ') || '—'}</td>
              </tr>
            ))}
            {!sorted.length && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-gray-400">No events.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && <EventSleevesModal reviewId={open.id} title={open.title} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
